<template>
  <NuxtLayout name="default">
    <section data-containerid="service_help">
      <QuidgetAiSection v-model:visible="isChatVisible" />
    </section>

    <section ref="linkList" class="mt-300" data-containerid="service_about">
      <HyperLinkModal v-if="showModal" @cancel="showModal = false" @confirm="handleConfirm" />
      <h3 class="mb-100">{{ t('service.headline.about') }}</h3>

      <LcCard>
        <LcLinkList>
          <LcLinkListItem
            v-for="(item, i) in items"
            :key="`link-list-item-${i}`"
            :class="{ external: /https?:\/\//.test(String(item.to)) }"
            v-bind="{ ...item }"
          />
        </LcLinkList>
      </LcCard>
    </section>

    <section class="mt-300" data-containerid="service_certifications">
      <TrustCertificatesSection />
    </section>
  </NuxtLayout>
</template>

<script lang="ts" setup>
import type { LcLinkListItemProps } from '@lottocom/frontend-components'

import { LicensedTerritory } from '@lottocom/lottery-mapping'
import { useEventListener } from '@vueuse/core'

definePageMeta({
  header: 'logo-only',
  layout: false,
})

const licensedTerritory = useLicensedTerritory({ raw: true })
const { t } = useI18n()
const { navigateToLocalized, removeQueryParams } = useRouterUtils()
const { query } = useRoute()

const linkListRef = useTemplateRef<HTMLElement>('linkList')
const showModal = ref(false)
const isChatVisible = ref(false)
const target = ref('')

const baseItems = computed<LcLinkListItemProps[]>(() => [
  {
    headline: t('service.menu.company.title'),
    icon: 'info',
    to: '/company',
  },
  {
    headline: t('service.menu.media.title'),
    icon: 'external-link',
    to: '/media',
  },
  {
    headline: t('service.menu.responsibleplay.title'),
    icon: 'verify-shield',
    to: '/responsible-play',
  },
  {
    headline: t('service.menu.careers.title'),
    icon: 'careers',
    target: '_blank',
    to: 'https://www.linkedin.com/jobs/lotto.com-inc.-jobs-worldwide',
  },
])

const items = computed<LcLinkListItemProps[]>(() => {
  switch (licensedTerritory) {
    case LicensedTerritory.US_FL:
      return [
        ...baseItems.value,
        {
          headline: t('service.menu.retailer.title'),
          icon: 'retailer',
          target: '_blank',
          to: 'https://floridalottery.com/where-to-play',
        },
      ]
    case LicensedTerritory.US_ME:
      return [
        ...baseItems.value,
        {
          headline: t('service.menu.retailer.title'),
          icon: 'retailer',
          target: '_blank',
          to: 'https://www.mainelottery.com/players_info/where_to_buy.html',
        },
      ]
    case LicensedTerritory.US_NY:
      return [
        ...baseItems.value,
        {
          headline: t('service.menu.retailer.title'),
          icon: 'retailer',
          target: '_blank',
          to: 'https://nylottery.ny.gov/find-a-retailer/',
        },
      ]
    default:
      return baseItems.value
  }
})

const handleClick = (event: Event) => {
  event.preventDefault()
  target.value = (event.currentTarget as HTMLAnchorElement).href
  showModal.value = true
}

const handleConfirm = async () => {
  await navigateToLocalized(target.value, {
    external: true,
    open: { target: '_blank' },
  })
  showModal.value = false
}

const initializedListeners = ref(false)

onMounted(async () => {
  if (query.chat === 'true') {
    await removeQueryParams('chat')
    isChatVisible.value = true
  }

  if (!initializedListeners.value) {
    const links = linkListRef.value?.querySelectorAll('.external a')
    if (links?.length) {
      links.forEach((element) => {
        useEventListener(element, 'click', handleClick)
      })
      initializedListeners.value = true
    }
  }
})
</script>
